/**
 * The empty chat's starter suggestions — the prompts offered under the
 * composer before the first message — chosen for the page the panel is
 * open on. The mechanism lives here; the starters themselves are listed in
 * `registry.ts` (P1's core ones in `coreStarters.ts`, each package's own
 * spread in next to them — the `views/registry.ts` pattern).
 */
import type { NsType, TranslationKey } from "@msys/tolgee";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { matchPath } from "react-router-dom";
import type { Capabilities } from "../../../../clients/graphqlTypes.js";
import { useUserData } from "../../../auth/useUserData.js";
import type { PageContext } from "../pageContext.js";

/** How many starters the empty chat shows at most. */
export const AGENT_STARTER_MAX = 4;

/** Where a starter's `order` puts it when it names none. */
const DEFAULT_ORDER = 100;

/**
 * One starter suggestion: a prompt the user can pick instead of typing.
 *
 * - `id` — unique across the registry, prefixed with the owning package
 *   (`core.whats-new`, `quotes.missing-prices`).
 * - `ns`/`textKey` — the prompt as a Tolgee key in the registering
 *   package's own namespace; its English text is the key (`en == key`).
 *   Picking the starter puts exactly this translated text into the
 *   composer. Build it with `defineAgentStarter`, which checks the key
 *   against that namespace at compile time.
 * - `match` — the pages it fits, judged on the panel's page context
 *   (`derivePageContext`, `pageContext.ts`): a route pattern via
 *   `onRoutes("/crm/*")`, or the entity open (`c => c.quoteId !== undefined`).
 *   Without one the starter is generic: it fits every page and fills the
 *   slots the page's own starters leave free.
 * - `order` — lower first within its group (the page's own starters, then
 *   the generic ones); default 100, ties keep registration order.
 * - `requiredCapabilities` — organisation capabilities (`Capabilities`, as
 *   `useUserData().hasCapability` takes them) the viewer's organisation
 *   must all have. Lacking any, the starter is never offered: it takes no
 *   slot and suppresses no other starter's text. Name the capabilities the
 *   starter's tool and page need (e.g. `["quoting"]` for a quote starter).
 *
 * Offer only what the assistant can do there with a tool its package ships
 * (and the page's own permission gate implies): a starter is a promise.
 */
export type AgentStarterSuggestion = {
  id: string;
  ns: NsType;
  textKey: string;
  order?: number;
  match?: (context: PageContext) => boolean;
  requiredCapabilities?: readonly Capabilities[];
};

/** A starter with its `textKey` checked against its own namespace `ns`. */
export function defineAgentStarter<Namespace extends NsType>(
  starter: Omit<AgentStarterSuggestion, "ns" | "textKey"> & {
    ns: Namespace;
    textKey: TranslationKey<Namespace> & string;
  }
): AgentStarterSuggestion {
  return starter;
}

/**
 * A `match` for route patterns (React Router's, as in the `*Routes.tsx`
 * files): the page fits when any pattern matches its path; a trailing
 * `/*` also takes every page below it.
 */
export function onRoutes(
  ...patterns: string[]
): (context: PageContext) => boolean {
  return context =>
    patterns.some(pattern => matchPath(pattern, context.path) !== null);
}

/**
 * The starters to show on `context`'s page. First every starter whose
 * `requiredCapabilities` the viewer's organisation lacks (`hasCapability`)
 * is dropped — before anything else, so it takes no slot and suppresses no
 * text. Of the rest: the ones whose `match` accepts the page, then the
 * generic ones (no `match`) — each group by `order`, then by registration —
 * without repeating an id or a text, cut to `max`.
 */
export function selectAgentStarters(
  starters: readonly AgentStarterSuggestion[],
  context: PageContext,
  hasCapability: (capability: Capabilities) => boolean,
  max: number = AGENT_STARTER_MAX
): AgentStarterSuggestion[] {
  const available = starters.filter(starter =>
    (starter.requiredCapabilities ?? []).every(capability =>
      hasCapability(capability)
    )
  );
  const byOrder = (group: AgentStarterSuggestion[]) =>
    group
      .map((starter, index) => ({ starter, index }))
      .sort(
        (a, b) =>
          (a.starter.order ?? DEFAULT_ORDER) -
            (b.starter.order ?? DEFAULT_ORDER) || a.index - b.index
      )
      .map(({ starter }) => starter);
  const pageOwn = available.filter(
    starter => starter.match !== undefined && starter.match(context)
  );
  const generic = available.filter(starter => starter.match === undefined);
  const seen = new Set<string>();
  return [...byOrder(pageOwn), ...byOrder(generic)]
    .filter(starter => {
      const text = `${starter.ns}:${starter.textKey}`;
      if (seen.has(starter.id) || seen.has(text)) return false;
      seen.add(starter.id);
      seen.add(text);
      return true;
    })
    .slice(0, max);
}

/**
 * The starters for `context`'s page as translated prompts — re-rendered on
 * a language switch like any other text. Starters needing a capability the
 * viewer's organisation lacks are left out (`useUserData().hasCapability`).
 * Loads every namespace the
 * registered starters use (a static list: the registry is fixed at build).
 */
export function useAgentStarterSuggestions(
  context: PageContext,
  starters: readonly AgentStarterSuggestion[]
): string[] {
  const namespaces = React.useMemo(
    () => Array.from(new Set(starters.map(starter => starter.ns))),
    [starters]
  );
  const { t } = useTranslate(namespaces);
  const { hasCapability } = useUserData();
  return React.useMemo(
    () =>
      selectAgentStarters(starters, context, hasCapability).map(starter =>
        // The key was checked against its namespace where the starter was
        // defined (`defineAgentStarter`); here it is only a string.
        t(starter.textKey as TranslationKey<typeof starter.ns>, {
          ns: starter.ns,
        })
      ),
    [starters, context, hasCapability, t]
  );
}
